import jsPDF from "jspdf";
import "jspdf-autotable";
import { addLogoToPdf } from "./utils/PdfUtils";
import dayjs from "dayjs";

const emptyPdfValue = (value) =>
  value === null || value === undefined || value === "" ? "-" : String(value);

const formatPdfDate = (value, format = "DD MMM YYYY") => {
  const parsedDate = dayjs(value);
  return parsedDate.isValid() ? parsedDate.format(format) : "-";
};

const formatPdfTime = (value) => {
  if (!value) return "-";
  if (typeof value === "string" && /^\d{1,2}:\d{2}/.test(value)) {
    return value.slice(0, 5);
  }

  const parsedTime = dayjs(value);
  return parsedTime.isValid() ? parsedTime.format("HH:mm") : "-";
};

const formatPdfName = (user = {}) =>
  [user?.firstname || user?.firstName, user?.name || user?.lastName]
    .filter(Boolean)
    .join(" ") ||
  user?.username ||
  user?.email ||
  "-";

const formatPdfSampleType = (type) => {
  if (!type) return "-";
  const text = String(type);
  return text.charAt(0).toUpperCase() + text.slice(1);
};

const formatPdfStatus = (status) =>
  (String(status || "").trim() || "draft").replace(/(^|\s)\S/g, (character) =>
    character.toUpperCase(),
  );

const samplePdfPatientLabel = (sample = {}) =>
  sample.patientCode ||
  sample.patientKey ||
  sample.patient?.patientCode ||
  sample.patient?.patient_code ||
  sample.patient?.code ||
  sample.patient?.number ||
  "-";

const samplePdfInclusionNumberLabel = (sample = {}) =>
  emptyPdfValue(
    sample.inclusionNumber ??
      sample.inclusion_number ??
      sample.patient?.inclusionNumber ??
      sample.patient?.inclusion_number,
  );

const samplePdfKitReferenceLabel = (sample = {}) =>
  sample.production?.reference ||
  sample.Production?.reference ||
  sample.productionReference ||
  sample.kitReference ||
  "-";

const samplePdfAnalysisLabel = (sample = {}) => {
  if (sample.analyses?.length) {
    return (
      sample.analyses
        .map((analysis) => analysis.name)
        .filter(Boolean)
        .join(", ") || "-"
    );
  }

  return sample.analysis?.name || sample.Analysis?.name || "-";
};

const samplePdfSampleAnalysisLabel = (sample = {}) => {
  const sampleLabel =
    sample.reference ||
    sample.sampleReference ||
    sample.sampleName ||
    sample.name ||
    "-";
  const analysisLabel = samplePdfAnalysisLabel(sample);
  const labels = [sampleLabel, analysisLabel].filter(
    (label) => label && label !== "-",
  );

  return labels.length ? labels.join(" / ") : "-";
};

const samplePdfVisitLabel = (sample = {}) => {
  const visitNames = (sample.visits || sample.Visits || [])
    .map((visit) => visit.name)
    .filter(Boolean);

  if (visitNames.length) {
    return [...new Set(visitNames)].join(", ");
  }

  return (
    sample.visit?.name ||
    sample.Visit?.name ||
    sample.visitName ||
    sample.visit_name ||
    "-"
  );
};

const samplePdfVisitListLabel = (sample = {}) => {
  const visitNames = (sample.visits || sample.Visits || [])
    .map((visit) => visit.name)
    .filter(Boolean);

  if (visitNames.length) {
    return [...new Set(visitNames)].join("\n");
  }

  return samplePdfVisitLabel(sample);
};

const samplePdfTypeLabel = (sample = {}) =>
  formatPdfSampleType(
    sample.sampleTypeDefinition?.name ||
      sample.SampleTypeDefinition?.name ||
      sample.sampleType?.name ||
      sample.SampleType?.name ||
      sample.sampleTypeName ||
      sample.type ||
      (typeof sample.sampleType === "string" ? sample.sampleType : "") ||
      sample.primaryType,
  );

const samplePdfQuantityLabel = (sample = {}) =>
  sample.quantity ?? sample.totalSamples ?? sample.quantitySent ?? "-";

const samplePdfReceptionCompleted = (sampleShipment = {}) =>
  Boolean(
    sampleShipment.receiveDatetime ||
      sampleShipment.reception?.receiveDatetime ||
      String(sampleShipment.status || "").trim().toLowerCase() === "received",
  );

const samplePdfReceivedQuantityLabel = (sample = {}, receptionCompleted) => {
  if (!receptionCompleted) return "";

  return (
    sample.quantityReceived ??
    sample.totalSamplesReceived ??
    sample.receivedQuantity ??
    samplePdfQuantityLabel(sample)
  );
};

const samplePdfContainerDetails = (sample = {}) => {
  const details = samplePdfAnalysisRecords(sample).map((analysis) =>
    samplePdfAnalysisContentDetails(analysis),
  );

  const joinUnique = (values) => {
    const uniqueValues = [...new Set(values.map(emptyPdfValue))].filter(
      (value) => value !== "-",
    );

    return uniqueValues.length ? uniqueValues.join(", ") : "-";
  };

  return {
    containerType: joinUnique(details.map((detail) => detail.containerType)),
    containerSize: joinUnique(details.map((detail) => detail.containerSize)),
    containerSizeUnit: joinUnique(
      details.map((detail) => detail.containerSizeUnit),
    ),
  };
};

const samplePdfContainerLabel = (sample = {}) => {
  const { containerType, containerSize, containerSizeUnit } =
    samplePdfContainerDetails(sample);

  const parts = [containerType, containerSize, containerSizeUnit].filter(
    (part) => part !== "-",
  );

  return parts.length ? parts.join(" ") : "-";
};

const samplePdfAddressSummary = (address = {}) => {
  const line1 = [address.hospital, address.ward, address.building]
    .filter(Boolean)
    .join(", ");
  const line2 = [
    address.street,
    address.zone,
    address.floor,
    address.zipcode,
    address.city,
    address.country,
  ]
    .filter(Boolean)
    .join(", ");

  return [
    line1 || "Address not completed",
    line2 || "Location details not completed",
  ];
};

const samplePdfAnalysisRecords = (sample = {}) => {
  const analyses = sample.analyses || sample.Analyses || [];

  if (analyses.length) return analyses;

  const analysis = sample.analysis || sample.Analysis;
  return analysis ? [analysis] : [{}];
};

const pdfHexColor = (value) => {
  const normalizedColor = String(value || "").trim();
  const match = /^#([0-9a-f]{2})([0-9a-f]{2})([0-9a-f]{2})$/i.exec(
    normalizedColor,
  );

  return match
    ? match.slice(1).map((channel) => Number.parseInt(channel, 16))
    : [0, 0, 0];
};

const samplePdfAnalysisTextColor = (sample = {}) => {
  const analysisWithColor = samplePdfAnalysisRecords(sample).find(
    (analysis) => analysis?.color,
  );

  return pdfHexColor(analysisWithColor?.color);
};

const samplePdfShipmentTemperatures = (samples = []) => {
  const temperatures = [];
  const seen = new Set();

  const addTemperature = (value) => {
    const temperature =
      typeof value === "string"
        ? value.trim()
        : String(value?.name || value?.temperatureName || "").trim();
    const key = temperature.toLowerCase();

    if (temperature && !seen.has(key)) {
      seen.add(key);
      temperatures.push(temperature);
    }
  };

  samples.forEach((sample) => {
    addTemperature(sample.shippingTemperature);
    samplePdfAnalysisRecords(sample).forEach((analysis) => {
      addTemperature(
        analysis.temperatureDefinition ||
          analysis.Temperature ||
          analysis.temperature,
      );
    });
  });

  return temperatures.length ? temperatures.join(", ") : "-";
};

const samplePdfAnalysisName = (analysis = {}) =>
  analysis.name ||
  analysis.analysisName ||
  analysis.sampleAnalysisName ||
  analysis.AnalysisName ||
  analysis.label ||
  "-";

const samplePdfAnalysisContentDetails = (analysis = {}) => {
  const container =
    analysis.analysisContainer ||
    analysis.container ||
    {};
  const rawContainerType =
    container.type ??
    container.containerType ??
    analysis.containerType ??
    analysis.container_type ??
    analysis.containerName;
  const rawContainerSize =
    container.size ??
    container.containerSize ??
    analysis.containerSize ??
    analysis.container_size;
  const rawContainerSizeUnit =
    container.sizeUnit ??
    container.size_unit ??
    container.containerSizeUnit ??
    analysis.containerSizeUnit ??
    analysis.container_size_unit;
  const numericContainerSize = Number(rawContainerSize);

  return {
    matrix: samplePdfAnalysisName(analysis),
    containerType: emptyPdfValue(rawContainerType),
    containerSize:
      rawContainerSize !== null &&
      rawContainerSize !== undefined &&
      rawContainerSize !== ""
        ? Number.isFinite(numericContainerSize)
          ? String(numericContainerSize)
          : String(rawContainerSize)
        : "-",
    containerSizeUnit: emptyPdfValue(rawContainerSizeUnit),
  };
};

const samplePdfContentRows = (samples = []) => {
  const groupedRows = new Map();

  samples.forEach((sample) => {
    const rawQuantity = Number(samplePdfQuantityLabel(sample));
    const quantity = Number.isFinite(rawQuantity) ? rawQuantity : 1;

    samplePdfAnalysisRecords(sample).forEach((analysis) => {
      const details = samplePdfAnalysisContentDetails(analysis);
      const key = [
        details.matrix,
        details.containerType,
        details.containerSize,
        details.containerSizeUnit,
      ].join("|");
      const currentRow = groupedRows.get(key);

      groupedRows.set(key, {
        ...details,
        numberOfSamples: (currentRow?.numberOfSamples || 0) + quantity,
      });
    });
  });

  return Array.from(groupedRows.values());
};

const samplePdfConsignee = (sampleShipment = {}) => {
  const destinationAddress =
    sampleShipment.destinationAddress ||
    sampleShipment.address?.destination ||
    {};
  const receivedBy =
    sampleShipment.reception?.receiveByUser ||
    sampleShipment.receiveByUser ||
    {};
  const lines = [];

  const addLine = (value) => {
    const normalizedValue = String(value || "").trim();
    if (
      normalizedValue &&
      !lines.some(
        (line) => line.toLowerCase() === normalizedValue.toLowerCase(),
      )
    ) {
      lines.push(normalizedValue);
    }
  };

  addLine(formatPdfName(receivedBy));
  addLine(destinationAddress.hospital);
  addLine(destinationAddress.ward);
  addLine(destinationAddress.building);
  addLine(destinationAddress.street);
  addLine(destinationAddress.zone);
  addLine(destinationAddress.floor);
  addLine(
    [destinationAddress.zipcode, destinationAddress.city]
      .filter(Boolean)
      .join(" "),
  );
  addLine(destinationAddress.country);

  const phone = receivedBy.phoneNumber || destinationAddress.contactPhone;
  const email = receivedBy.email || destinationAddress.contactEmail;
  if (phone) addLine(`Phone: ${phone}`);
  if (email) addLine(`Email: ${email}`);

  return lines.join("\n");
};

const samplePdfShipperCenter = (sampleShipment = {}) =>
  sampleShipment.center?.centerNumber ||
  sampleShipment.pickupAddress?.centerNumber ||
  sampleShipment.address?.centerNumber ||
  sampleShipment.centerNumber ||
  "-";

export const printSampleShipmentSamples = (sampleShipment = {}, study = {}) => {
  const pdf = new jsPDF("l", "mm", "a4");
  const samples =
    sampleShipment.samples || sampleShipment.shipmentSamples || [];
  const shipmentNumber =
    sampleShipment.reference || sampleShipment.shipmentNumber || "-";
  const sponsorStudyCode =
    study?.sponsorStudyCode || sampleShipment.sponsorStudyCode || "-";
  const pageWidth = pdf.internal.pageSize.getWidth();
  const pageHeight = pdf.internal.pageSize.getHeight();
  const contentRows = samplePdfContentRows(samples);
  const consignee = samplePdfConsignee(sampleShipment);
  const shipperCenter = samplePdfShipperCenter(sampleShipment);
  const tableRows = (
    contentRows.length
      ? contentRows
      : [
          {
            matrix: "-",
            numberOfSamples: "-",
            containerType: "-",
            containerSize: "-",
            containerSizeUnit: "-",
          },
        ]
  ).map((row, index, rows) => [
    ...(index === 0
      ? [
          {
            content: consignee,
            rowSpan: rows.length,
            styles: { halign: "left", valign: "middle" },
          },
        ]
      : []),
    row.matrix,
    String(row.numberOfSamples),
    row.containerType,
    row.containerSize,
    row.containerSizeUnit,
  ]);

  pdf.setFont("helvetica", "normal");
  pdf.setFontSize(14);
  pdf.text("LIST OF CONTENTS", pageWidth / 2, 18, { align: "center" });
  pdf.text(`STUDY PROTOCOL ${sponsorStudyCode}`, pageWidth / 2, 28, {
    align: "center",
  });
  pdf.text("BIOLOGICAL SUBSTANCE, CATEGORY B, UN3373", pageWidth / 2, 38, {
    align: "center",
  });

  pdf.setFontSize(10);
  pdf.setFont("helvetica", "bold");
  pdf.text("SHIPPER:", 15, 53);
  pdf.setFont("helvetica", "normal");
  pdf.text(`site ${shipperCenter}`, 34, 53);

  pdf.autoTable({
    startY: 63,
    head: [
      [
        "Consignee",
        "Matrix",
        "Number of samples",
        "Container type",
        "Container size",
        "Container size unit",
      ],
    ],
    body: tableRows,
    theme: "grid",
    margin: { left: 15, right: 15 },
    styles: {
      font: "helvetica",
      fontSize: 9,
      cellPadding: 3,
      minCellHeight: 22,
      valign: "middle",
      halign: "center",
      lineColor: [90, 90, 90],
      lineWidth: 0.25,
      textColor: [20, 20, 20],
    },
    headStyles: {
      fillColor: [80, 80, 80],
      textColor: [255, 255, 255],
      fontStyle: "bold",
      minCellHeight: 14,
    },
    columnStyles: {
      0: { cellWidth: 65 },
      1: { cellWidth: 72 },
      2: { cellWidth: 30 },
      3: { cellWidth: 34 },
      4: { cellWidth: 28 },
      5: { cellWidth: 35 },
    },
  });

  const footer =
    "This item list must be placed between the secondary package and outer packaging in accordance with IATA Packing Instruction 650.";
  const footerLines = pdf.splitTextToSize(footer, pageWidth - 30);
  const footerHeight = footerLines.length * 4;
  let footerY = Math.max(
    (pdf.lastAutoTable?.finalY || 63) + 10,
    pageHeight - 10 - footerHeight,
  );

  if (footerY + footerHeight > pageHeight - 8) {
    pdf.addPage();
    footerY = 18;
  }

  pdf.setFont("helvetica", "bolditalic");
  pdf.setFontSize(8);
  pdf.text(footerLines, 15, footerY);

  const safeShipmentNumber = String(shipmentNumber).replace(
    /[^a-z0-9_-]+/gi,
    "_",
  );
  pdf.save(`sample_shipment_samples_${safeShipmentNumber}.pdf`);
};

const samplePdfAuditRows = (sampleShipment = {}) => [
  [
    "Request",
    formatPdfName(
      sampleShipment.user ||
        sampleShipment.User ||
        sampleShipment.createdByUser,
    ),
    formatPdfDate(sampleShipment.sendingDate || sampleShipment.createdAt),
  ],
  [
    "Confirm",
    formatPdfName(sampleShipment.confirmedByUser),
    formatPdfDate(sampleShipment.confirmedDatetime, "DD MMM YYYY HH:mm"),
  ],
  [
    "Receive",
    formatPdfName(
      sampleShipment.receiveByUser || sampleShipment.reception?.receiveByUser,
    ),
    formatPdfDate(
      sampleShipment.receiveDatetime ||
        sampleShipment.reception?.receiveDatetime,
      "DD MMM YYYY HH:mm",
    ),
  ],
];

const hasPdfAuditValue = ([, person, date]) => person !== "-" || date !== "-";

export const printSampleShipmentOrder = (sampleShipment, study) => {
  const pdf = new jsPDF("p", "mm", "a4"); // A4 size page of PDF
  const logoHeight = addLogoToPdf(pdf);
  const pageWidth = pdf.internal.pageSize.getWidth();

  const pageCenter = pdf.internal.pageSize.getWidth() / 2;
  pdf.setFontSize(12);
  const text = "Sample Shipment Order Form";
  const textHeight = 12;
  let y = logoHeight + 30;
  let leftMargin = 15;
  const rectWidth = pageWidth - leftMargin * 2;

  pdf.setFillColor(173, 216, 230);
  pdf.rect(leftMargin, y - textHeight, rectWidth, textHeight, "F");

  const textY = y - textHeight + pdf.internal.getLineHeight() / 2;
  pdf.text(text.toUpperCase(), pageCenter, textY, { align: "center" });

  y += 10;
  // Sponsor Study Code  + Sponsor name in parentheses
  pdf.setFontSize(10);
  pdf.text(`Study Code: ${study.sponsorStudyCode}`, leftMargin, y);
  pdf.text(`Sponsor: ${study.sponsor.name}`, leftMargin + 100, y);
  y += 6;
  // CDL study code
  pdf.text(`CDL Study Code: ${study.cdlStudyCode}`, leftMargin, y);

  y += 6;
  // Center and underline "Samples Pick Up Address"
  pdf.setFontSize(12);
  pdf.text("Samples Pick Up Address", pageCenter, y, { align: "center" });
  const textWidth = pdf.getTextWidth("Samples Pick Up Address");
  pdf.line(
    pageCenter - textWidth / 2,
    y + 1,
    pageCenter + textWidth / 2,
    y + 1,
  ); // Underline
  y += 10;

  pdf.setFontSize(10);
  let columnY = y;
  // Column 1
  pdf.text("Reference:", leftMargin, y);
  pdf.text(sampleShipment?.reference, leftMargin + 25, y);
  y += 6;

  pdf.text("Center:", leftMargin, y);
  pdf.text(sampleShipment?.address?.centerNumber || "N/A", leftMargin + 25, y);
  y += 6;

  pdf.text("Ward:", leftMargin, y);
  pdf.text(sampleShipment?.address?.ward || "N/A", leftMargin + 25, y);
  y += 6;

  pdf.text("Street:", leftMargin, y);
  pdf.text(sampleShipment?.address?.street || "N/A", leftMargin + 25, y);
  y += 6;

  pdf.text("City:", leftMargin, y);
  pdf.text(sampleShipment?.address?.city || "N/A", leftMargin + 25, y);
  y += 12;

  // Column 2
  const columnX = leftMargin + 90; // Start the second column further right
  pdf.text("Hospital:", columnX, columnY);
  pdf.text(sampleShipment?.address?.hospital || "N/A", columnX + 20, columnY);
  columnY += 6;

  pdf.text("Building:", columnX, columnY);
  pdf.text(sampleShipment?.address?.building || "N/A", columnX + 20, columnY);
  columnY += 6;

  pdf.text("Floor:", columnX, columnY);
  pdf.text(sampleShipment?.address?.floor || "N/A", columnX + 20, columnY);
  columnY += 6;

  pdf.text("Zipcode:", columnX, columnY);
  pdf.text(sampleShipment?.address?.zipcode || "N/A", columnX + 20, columnY);
  columnY += 6;

  pdf.text("Country:", columnX, columnY);
  pdf.text(sampleShipment?.address?.country || "N/A", columnX + 20, columnY);
  y = columnY + 12; // Update Y for the next section

  // Center and underline "Samples to Ship"
  pdf.setFontSize(12);
  pdf.text("Samples to Ship", pageCenter, y, { align: "center" });
  const samplesTextWidth = pdf.getTextWidth("Samples to Ship");
  pdf.line(
    pageCenter - samplesTextWidth / 2,
    y + 1,
    pageCenter + samplesTextWidth / 2,
    y + 1,
  ); // Underline
  y += 10;

  // Iterate through patients and create a section for each
  sampleShipment.patients?.forEach((patient, patientIndex) => {
    // Add a section title for the patient
    pdf.setFontSize(12);
    pdf.text(`Patient ID: ${patient.number}`, leftMargin, y);
    y += 6;

    // Prepare table data for the patient's analyses
    const tableData = [];
    patient.samples?.forEach((sample) => {
      tableData.push([
        {
          content: sample.analysis?.name || "-",
          styles: { textColor: samplePdfAnalysisTextColor(sample) },
        },
        sample.sampleType ? "Primary" : "Backup",
        sample.visits?.map((visit) => visit.name).join(", "),
        sample.analysis.temperature?.name,
        sample.quantitySent,
      ]);
    });

    // Add the table for the patient
    pdf.autoTable({
      startY: y,
      head: [
        [
          "Analysis",
          "PRIM / BU",
          "Visits",
          "Shipping Temp",
          "Total Samples",
        ],
      ],
      body: tableData,
      styles: { fontSize: 8, cellPadding: 2 },
      headStyles: {
        fillColor: [173, 216, 230],
        textColor: 0,
        fontSize: 10,
        fontStyle: "normal",
      },
      columnStyles: {
        0: { cellWidth: 45 }, // Analysis
        1: { cellWidth: 35 }, // PRIM / BU
        2: { cellWidth: 40 }, // Visits
        3: { cellWidth: 30 }, // Shipping Temp
        4: { cellWidth: 30 }, // Total Samples
      },
    });

    // Update the Y position for the next patient
    y = pdf.lastAutoTable.finalY + 10;

    // Add a page break if the content exceeds the page height
    if (y > pdf.internal.pageSize.getHeight() - 20) {
      pdf.addPage();
      y = 10; // Reset Y position for the new page
    }
  });
  // Transport Information
  // Center and underline "Transport Information"
  pdf.setFontSize(12);
  pdf.text("Transport Information", pageCenter, y, { align: "center" });
  const transportTextWidth = pdf.getTextWidth("Transport Information");
  pdf.line(
    pageCenter - transportTextWidth / 2,
    y + 1,
    pageCenter + transportTextWidth / 2,
    y + 1,
  ); // Underline
  y += 10;
  pdf.setFontSize(10);
  pdf.text("Expected Shipping Date:", leftMargin, y);

  // Format the date and time
  const formattedDate = dayjs(sampleShipment.expectedShippingDate).format(
    "DD MMM YYYY",
  );
  const formattedPickupStart = sampleShipment.pickupStart.slice(0, 5); // Remove seconds
  const formattedPickupEnd = sampleShipment.pickupEnd.slice(0, 5); // Remove seconds

  pdf.text(
    `${formattedDate}, between ${formattedPickupStart} and ${formattedPickupEnd}`,
    leftMargin + 50,
    y,
  );
  y += 12;

  // Person Requesting Shipment
  // Center and underline "Person Requesting Shipment"
  pdf.setFontSize(12);
  pdf.text("Person Requesting Shipment", pageCenter, y, { align: "center" });
  const personTextWidth = pdf.getTextWidth("Person Requesting Shipment");
  pdf.line(
    pageCenter - personTextWidth / 2,
    y + 1,
    pageCenter + personTextWidth / 2,
    y + 1,
  ); // Underline
  y += 10;
  pdf.setFontSize(10);
  pdf.text("Name:", leftMargin, y);
  pdf.text(`${sampleShipment.address.contactName}`, leftMargin + 50, y);
  y += 6;
  pdf.text("Email:", leftMargin, y);
  pdf.text(sampleShipment.address.contactEmail, leftMargin + 50, y);
  y += 6;
  pdf.text("Phone:", leftMargin, y);
  pdf.text(sampleShipment.address.contactPhone, leftMargin + 50, y);
  y += 6;
  pdf.text("Date:", leftMargin, y);
  // format sampleShipment.sendingDate
  const formattedSendingDate = dayjs(sampleShipment.sendingDate).format(
    "DD MMM YYYY",
  );
  pdf.text(formattedSendingDate, leftMargin + 50, y);

  y += 12;

  // Save PDF
  pdf.save(
    `sample_shipment_order_${sampleShipment.address.centerNumber}_${sampleShipment.reference}.pdf`,
  );
};

export const printSampleShipmentConfirmation = (
  sampleShipment,
  shipmentOrder,
  study,
) => {
  const pdf = new jsPDF("p", "mm", "a4"); // A4 size page of PDF
  const logoHeight = addLogoToPdf(pdf);
  const pageWidth = pdf.internal.pageSize.getWidth();
  const pageCenter = pageWidth / 2;

  sampleShipment.patients?.forEach((patient, patientIndex) => {
    let y = logoHeight + 30;
    let leftMargin = 10;
    const rectWidth = pageWidth - leftMargin * 2;

    // Header
    pdf.setFontSize(12);
    const text = "Sample Shipment Confirmation Form";
    const textHeight = 12;
    pdf.setFillColor(173, 216, 230);
    pdf.rect(leftMargin, y - textHeight, rectWidth, textHeight, "F");
    const textY = y - textHeight + pdf.internal.getLineHeight() / 2;
    pdf.text(text.toUpperCase(), pageCenter, textY, { align: "center" });
    y += 10;

    // Study info
    pdf.setFontSize(10);
    pdf.text(`Study Code: ${study.sponsorStudyCode}`, leftMargin, y);
    pdf.text(`Sponsor: ${study.sponsor.name}`, leftMargin + 100, y);
    y += 6;
    pdf.text(`CDL Study Code: ${study.cdlStudyCode}`, leftMargin, y);
    y += 6;

    // Shipment Information
    pdf.setFontSize(12);
    pdf.text("Shipment Information", pageCenter, y, { align: "center" });
    const shipmentInfoTextWidth = pdf.getTextWidth("Shipment Information");
    pdf.line(
      pageCenter - shipmentInfoTextWidth / 2,
      y + 1,
      pageCenter + shipmentInfoTextWidth / 2,
      y + 1,
    );
    y += 10;

    leftMargin = 15;
    const rightColumnX = leftMargin + 70;
    pdf.setFontSize(10);
    let infoY = y;

    // Row 1: Reference | Center
    pdf.text("Reference:", leftMargin, infoY);
    pdf.text(sampleShipment?.reference || "N/A", leftMargin + 25, infoY);
    pdf.text("Center:", rightColumnX, infoY);
    pdf.text(
      sampleShipment?.address?.centerNumber || "N/A",
      rightColumnX + 25,
      infoY,
    );
    infoY += 6;

    // Row 2: Carrier | Delivery Place
    pdf.text("Carrier:", leftMargin, infoY);
    pdf.text(shipmentOrder?.carrier || "N/A", leftMargin + 25, infoY);
    pdf.text("Delivery Place:", rightColumnX, infoY);
    pdf.text(
      sampleShipment?.address?.deliveryPlace || "N/A",
      rightColumnX + 25,
      infoY,
    );
    infoY += 6;

    // Row 3: Airwaybill
    pdf.text("Airwaybill:", leftMargin, infoY);
    pdf.text(
      shipmentOrder?.airwaybill || sampleShipment?.airwaybill || "N/A",
      leftMargin + 25,
      infoY,
    );
    infoY += 12;

    y = infoY;

    pdf.text("Pickup Date:", leftMargin, y);
    const formattedPickupDate = dayjs(sampleShipment.pickupDate).format(
      "DD MMM YYYY",
    );
    const formattedPickupStart = sampleShipment.pickupStart.slice(0, 5);
    const formattedPickupEnd = sampleShipment.pickupEnd.slice(0, 5);
    pdf.text(
      `${formattedPickupDate}, between ${formattedPickupStart} and ${formattedPickupEnd}`,
      leftMargin + 50,
      y,
    );
    y += 12;

    // Patient section
    pdf.setFontSize(12);
    pdf.text(`Patient ID: ${patient.number}`, leftMargin, y);
    y += 6;

    // Table for this patient
    const tableData = [];
    patient.samples?.forEach((sample) => {
      tableData.push([
        sample.analysis?.name,
        sample.sampleType ? "Primary" : "Backup",
        sample.visits?.map((visit) => visit.name).join(", "),
        sample.analysis.temperature?.name,
        sample.quantitySent,
        sample.quantityReceived === null ||
        sample.quantityReceived === undefined
          ? ""
          : sample.quantityReceived,
      ]);
    });

    pdf.autoTable({
      startY: y,
      head: [
        [
          "Analysis",
          "PRIM / BU",
          "Visits",
          "Shipping Temperature",
          "Nb of samples sent",
          "Nb of samples received",
        ],
      ],
      body: tableData,
      styles: {
        fontSize: 8,
        cellPadding: 2,
        lineColor: [0, 0, 0],
        lineWidth: 0.3,
      },
      headStyles: {
        fillColor: [173, 216, 230],
        textColor: 0,
        fontSize: 10,
        fontStyle: "normal",
        lineColor: [0, 0, 0],
        lineWidth: 0.3,
      },
      columnStyles: {
        0: { cellWidth: 35 },
        1: { cellWidth: 25 },
        2: { cellWidth: 40 },
        3: { cellWidth: 25 },
        4: { cellWidth: 25 },
        5: { cellWidth: 30 },
      },
    });

    y = pdf.lastAutoTable.finalY + 10;

    // === FRAMEWORK RESERVED TO RECIPIENT RECTANGLE ===
    const frameworkRectTop = y;
    const frameworkRectLeft = leftMargin;
    const frameworkRectWidth = pageWidth - leftMargin * 2;

    pdf.setDrawColor(0, 0, 0);
    pdf.setLineWidth(0.5);

    // Title for the rectangle
    const frameworkTitle = "FRAMEWORK RESERVED TO RECIPIENT";
    pdf.setFontSize(11);
    const titleWidth = pdf.getTextWidth(frameworkTitle);
    const titleX = pageCenter - titleWidth / 2;
    pdf.text(frameworkTitle, titleX, y + 6);

    y += 12;

    // "Biobanking of samples" title and underline
    pdf.setFontSize(12);
    pdf.text("Biobanking of samples", pageCenter, y, { align: "center" });
    const biobankingTextWidth = pdf.getTextWidth("Biobanking of samples");
    pdf.line(
      pageCenter - biobankingTextWidth / 2,
      y + 1,
      pageCenter + biobankingTextWidth / 2,
      y + 1,
    );
    pdf.setFontSize(10);

    y += 6;

    // Biobanking section as a table
    const biobankingRows = [
      [
        "Biobanking Date:",
        shipmentOrder.biobankingDate
          ? dayjs(shipmentOrder.biobankingDate).format("DD MMM YYYY")
          : "",
      ],
      ["Biobanking Initials:", shipmentOrder.biobankingInitials || ""],
      [
        "Primary Samples Storage Location:",
        shipmentOrder.primaryStorageLocation || "",
      ],
      [
        "Backup Samples Storage Location:",
        shipmentOrder.backupStorageLocation || "",
      ],
      ["Comments:", shipmentOrder.comments || ""],
    ];

    pdf.autoTable({
      startY: y,
      head: [["", ""]],
      body: biobankingRows,
      styles: { fontSize: 10, cellPadding: 2, minCellHeight: 10 },
      columnStyles: {
        0: { cellWidth: 70 },
        1: { cellWidth: pageWidth - leftMargin * 2 - 70 },
      },
      theme: "plain",
      didDrawCell: function (data) {
        if (
          data.section === "body" &&
          data.column.index === 1 &&
          data.row.index < 4 &&
          !biobankingRows[data.row.index][1]
        ) {
          const { x, y, width, height } = data.cell;
          pdf.rect(x + 1, y + 1, width - 2, height - 2);
        }
        if (
          data.section === "body" &&
          data.column.index === 1 &&
          data.row.index === 4 &&
          !biobankingRows[4][1]
        ) {
          const { x, y, width } = data.cell;
          pdf.rect(x + 1, y + 1, width - 2, 15);
        }
      },
    });

    const frameworkRectBottom = pdf.lastAutoTable.finalY + 10;
    const frameworkRectHeight = frameworkRectBottom - frameworkRectTop;
    pdf.setDrawColor(0, 0, 0);
    pdf.setLineWidth(0.5);
    pdf.rect(
      frameworkRectLeft,
      frameworkRectTop,
      frameworkRectWidth,
      frameworkRectHeight,
    );

    y = frameworkRectBottom + 10;

    y += 12;

    // Add a page break if not the last patient
    if (patientIndex < sampleShipment.patients.length - 1) {
      pdf.addPage();
    }
  });

  // Save PDF
  pdf.save(
    `sample_shipment_confirmation_${sampleShipment.address.centerNumber}_${sampleShipment.reference}.pdf`,
  );
};

export const printSampleShipmentReception = (
  sampleShipment = {},
  study = {},
) => {
  const pdf = new jsPDF("p", "mm", "a4");
  const logoHeight = addLogoToPdf(pdf);
  const pageWidth = pdf.internal.pageSize.getWidth();
  const pageCenter = pageWidth / 2;
  const leftMargin = 15;
  const rectWidth = pageWidth - leftMargin * 2;
  const reception = sampleShipment.reception || {};
  let y = logoHeight + 30;

  pdf.setFontSize(12);
  pdf.setFillColor(173, 216, 230);
  pdf.rect(leftMargin, y - 12, rectWidth, 12, "F");
  pdf.text("SAMPLE SHIPMENT RECEPTION", pageCenter, y - 4, {
    align: "center",
  });

  y += 8;
  pdf.setFontSize(10);
  pdf.text(`CDL Study Code: ${study?.cdlStudyCode || "-"}`, leftMargin, y);
  pdf.text(
    `Sponsor Study Code: ${study?.sponsorStudyCode || "-"}`,
    leftMargin + 95,
    y,
  );
  y += 10;

  pdf.setFontSize(12);
  pdf.text("Shipment Information", pageCenter, y, { align: "center" });
  const titleWidth = pdf.getTextWidth("Shipment Information");
  pdf.line(
    pageCenter - titleWidth / 2,
    y + 1,
    pageCenter + titleWidth / 2,
    y + 1,
  );
  y += 8;

  pdf.autoTable({
    startY: y,
    body: [
      [
        "Center ID",
        sampleShipment.centerNumber ||
          sampleShipment.address?.centerNumber ||
          "-",
      ],
      ["Shipment Number", sampleShipment.reference || "-"],
      ["Carrier", sampleShipment.carrier || "-"],
      [
        "Airwaybill",
        sampleShipment.airwaybill || sampleShipment.trackingNumber || "-",
      ],
      ["Number of Packages", sampleShipment.numberOfPackages ?? "-"],
      ["Temperature probe", reception.temperatureProbe ? "Yes" : "No"],
      ["Sonde", reception.sonde || "-"],
      ["Conforme", reception.conforme ? "Yes" : "No"],
      ["Comments", reception.comments || "-"],
    ],
    theme: "grid",
    styles: { fontSize: 9, cellPadding: 2 },
    columnStyles: {
      0: { cellWidth: 55, fontStyle: "bold" },
      1: { cellWidth: rectWidth - 55 },
    },
  });

  y = pdf.lastAutoTable.finalY + 10;
  pdf.setFontSize(12);
  pdf.text("Samples Reception", pageCenter, y, { align: "center" });
  const samplesTitleWidth = pdf.getTextWidth("Samples Reception");
  pdf.line(
    pageCenter - samplesTitleWidth / 2,
    y + 1,
    pageCenter + samplesTitleWidth / 2,
    y + 1,
  );
  y += 8;

  pdf.autoTable({
    startY: y,
    head: [
      [
        "Shipping Temperature",
        "Total Samples Sent (Theory)",
        "Total Samples Received",
        "Conforme",
        "Comments",
      ],
    ],
    body: (reception.items || []).map((item) => [
      item.shippingTemperature || "-",
      item.quantitySent ?? "",
      item.quantityReceived ?? "",
      item.conforme ? "Yes" : "No",
      item.comments || "",
    ]),
    styles: { fontSize: 8, cellPadding: 2 },
    headStyles: {
      fillColor: [173, 216, 230],
      textColor: 0,
      fontStyle: "normal",
    },
  });

  y = pdf.lastAutoTable.finalY + 10;
  pdf.setFontSize(12);
  pdf.text("Storage", pageCenter, y, { align: "center" });
  const storageTitleWidth = pdf.getTextWidth("Storage");
  pdf.line(
    pageCenter - storageTitleWidth / 2,
    y + 1,
    pageCenter + storageTitleWidth / 2,
    y + 1,
  );
  y += 8;

  pdf.autoTable({
    startY: y,
    body: [
      [
        "Storage Date/Time",
        reception.storageDatetime
          ? dayjs(reception.storageDatetime).format("DD MMM YYYY HH:mm")
          : "-",
      ],
      ["Storage Chamber", reception.storageChamber || "-"],
      ["Storage Shelf", reception.storageShelf || "-"],
    ],
    theme: "grid",
    styles: { fontSize: 9, cellPadding: 2 },
    columnStyles: {
      0: { cellWidth: 55, fontStyle: "bold" },
      1: { cellWidth: rectWidth - 55 },
    },
  });

  pdf.save(
    `sample_shipment_reception_${sampleShipment.centerNumber || sampleShipment.address?.centerNumber || "center"}_${sampleShipment.reference || "shipment"}.pdf`,
  );
};

export const printSampleShipmentDetail = (
  sampleShipment = {},
  study = {},
) => {
  const pdf = new jsPDF("p", "mm", "a4");
  const logoHeight = addLogoToPdf(pdf);
  const pageWidth = pdf.internal.pageSize.getWidth();
  const pageHeight = pdf.internal.pageSize.getHeight();
  const pageCenter = pageWidth / 2;
  const leftMargin = 15;
  const rectWidth = pageWidth - leftMargin * 2;
  let y = logoHeight + 30;

  const pickupAddress =
    sampleShipment.pickupAddress || sampleShipment.address || {};
  const destinationAddress =
    typeof sampleShipment.destinationAddress === "object"
      ? sampleShipment.destinationAddress || {}
      : {};
  const samples =
    sampleShipment.samples || sampleShipment.shipmentSamples || [];
  const pickupStart =
    sampleShipment.pickupDatetimeStart ||
    sampleShipment.expectedShippingDate ||
    sampleShipment.pickupDate;
  const pickupEnd =
    sampleShipment.pickupDatetimeEnd || sampleShipment.pickupEnd;
  const sponsorStudyCode =
    study?.sponsorStudyCode ||
    sampleShipment.sponsorStudyCode ||
    sampleShipment.studyCode ||
    "-";
  const cdlStudyCode =
    study?.cdlStudyCode || sampleShipment.cdlStudyCode || "-";
  const shipmentTemperatures = samplePdfShipmentTemperatures(samples);
  const confirmationDate = formatPdfDate(
    sampleShipment.confirmedDatetime,
    "DD/MMM/YYYY",
  ).toUpperCase();
  const centerNumber =
    pickupAddress.centerNumber || sampleShipment.centerNumber;
  const deliveryPlace =
    destinationAddress.title ||
    destinationAddress.hospital ||
    destinationAddress.deliveryPlace ||
    sampleShipment.deliveryPlace ||
    "CDL PHARMA";
  const boxDescription = emptyPdfValue(sampleShipment.boxType);
  const pickupDate = formatPdfDate(pickupStart, "DD/MMM/YYYY").toUpperCase();
  const pickupWindow = `${pickupDate}  ${formatPdfTime(
    pickupStart,
  )} – ${formatPdfTime(pickupEnd)}`;
  const receptionCompleted = samplePdfReceptionCompleted(sampleShipment);
  const patientGroups = Array.from(
    samples
      .reduce((groups, sample) => {
        const patientLabel = samplePdfPatientLabel(sample);
        const inclusionNumber = samplePdfInclusionNumberLabel(sample);
        const patientKey =
          sample.patientId ||
          sample.patient?.id ||
          patientLabel ||
          "unassigned";
        const group = groups.get(patientKey) || {
          label: patientLabel,
          inclusionNumber,
          samples: [],
        };

        if (group.inclusionNumber === "-" && inclusionNumber !== "-") {
          group.inclusionNumber = inclusionNumber;
        }

        group.samples.push(sample);
        groups.set(patientKey, group);
        return groups;
      }, new Map())
      .values(),
  );

  pdf.setFont("helvetica", "normal");
  pdf.setFontSize(10);
  pdf.text(
    `CDL Pharma study code: ${cdlStudyCode}`,
    pageWidth - leftMargin,
    18,
    { align: "right" },
  );

  pdf.setFont("helvetica", "bold");
  pdf.setFontSize(11);
  pdf.setFillColor(173, 216, 230);
  pdf.rect(leftMargin, y - 8, rectWidth, 12, "F");
  pdf.text(
    `SHIPMENT CONFIRMATION FORM - STUDY ${sponsorStudyCode}`,
    pageCenter,
    y - 0.5,
    { align: "center" },
  );
  y += 9;

  const confirmationTableOptions = {
    styles: {
      cellPadding: 2.5,
      font: "helvetica",
      fontSize: 9,
      lineColor: [0, 0, 0],
      lineWidth: 0.25,
      minCellHeight: 10,
      textColor: [0, 0, 0],
      valign: "middle",
    },
    margin: { left: leftMargin, right: leftMargin },
    theme: "grid",
  };

  pdf.autoTable({
    ...confirmationTableOptions,
    startY: y,
    body: [
      [
        {
          content: "Date of shipment confirmation:",
          styles: { fontStyle: "bold" },
        },
        confirmationDate,
        { content: "Center ID", styles: { fontStyle: "bold" } },
        emptyPdfValue(centerNumber),
      ],
    ],
    columnStyles: {
      0: { cellWidth: 65 },
      1: { cellWidth: 47 },
      2: { cellWidth: 26 },
      3: { cellWidth: rectWidth - 138 },
    },
  });

  pdf.autoTable({
    ...confirmationTableOptions,
    startY: pdf.lastAutoTable.finalY,
    body: [
      [
        {
          content: "SHIPMENT INFORMATION",
          styles: {
            fillColor: [211, 211, 211],
            fontStyle: "bold",
            halign: "center",
          },
        },
      ],
    ],
    columnStyles: {
      0: { cellWidth: rectWidth },
    },
  });

  pdf.autoTable({
    ...confirmationTableOptions,
    startY: pdf.lastAutoTable.finalY,
    body: [
      [
        { content: "Courier:", styles: { fontStyle: "bold" } },
        emptyPdfValue(sampleShipment.carrier),
        { content: "Delivery place:", styles: { fontStyle: "bold" } },
        emptyPdfValue(deliveryPlace),
      ],
      [
        { content: "Type of box", styles: { fontStyle: "bold" } },
        boxDescription,
        {
          content: "Shipment temperature",
          styles: { fontStyle: "bold" },
        },
        shipmentTemperatures,
      ],
      [
        { content: "AIRWAYBILL", styles: { fontStyle: "bold" } },
        {
          content: emptyPdfValue(
            sampleShipment.airwaybill || sampleShipment.trackingNumber,
          ),
          styles: { fontStyle: "bold" },
        },
        {
          content: "Temperature tracker",
          styles: { fontStyle: "bold" },
        },
        sampleShipment.temperatureProbe ? "YES" : "NO",
      ],
    ],
    columnStyles: {
      0: { cellWidth: 36 },
      1: { cellWidth: 42 },
      2: { cellWidth: 55 },
      3: { cellWidth: rectWidth - 133 },
    },
  });

  pdf.autoTable({
    ...confirmationTableOptions,
    startY: pdf.lastAutoTable.finalY,
    body: [
      [
        {
          content: `Pick-up date and time: ${pickupWindow}`,
          styles: { fontStyle: "bold" },
        },
      ],
    ],
    columnStyles: {
      0: { cellWidth: rectWidth },
    },
  });

  y = pdf.lastAutoTable.finalY + 10;
  pdf.setFont("helvetica", "normal");

  if (!patientGroups.length) {
    patientGroups.push({ label: "-", inclusionNumber: "-", samples: [] });
  }

  patientGroups.forEach((patientGroup) => {
    if (y > pageHeight - 35) {
      pdf.addPage();
      y = 20;
    }

    pdf.autoTable({
      startY: y,
      head: [
        [
          {
            content: `Patient: ${patientGroup.label || "-"} | Inclusion Number: ${
              patientGroup.inclusionNumber || "-"
            }`,
            colSpan: 5,
            styles: {
              fillColor: [173, 216, 230],
              fontSize: 10,
              fontStyle: "bold",
              halign: "left",
            },
          },
        ],
        [
          "Sample / Analyses",
          "PRIM / BU",
          "Visits",
          "Number of samples sent",
          "Number of samples received",
        ],
      ],
      body: patientGroup.samples.length
        ? patientGroup.samples.map((sample) => [
            {
              content: samplePdfSampleAnalysisLabel(sample),
              styles: {
                fontStyle: "bold",
                textColor: samplePdfAnalysisTextColor(sample),
              },
            },
            samplePdfTypeLabel(sample),
            samplePdfVisitListLabel(sample),
            samplePdfQuantityLabel(sample),
            samplePdfReceivedQuantityLabel(sample, receptionCompleted),
          ])
        : [["-", "-", "-", "-", ""]],
      margin: { left: leftMargin, right: leftMargin },
      rowPageBreak: "avoid",
      styles: {
        cellPadding: 2,
        fontSize: 8,
        lineColor: [0, 0, 0],
        lineWidth: 0.2,
        valign: "middle",
      },
      headStyles: {
        fillColor: [173, 216, 230],
        fontStyle: "normal",
        textColor: 0,
      },
      columnStyles: {
        0: { cellWidth: 48 },
        1: { cellWidth: 26 },
        2: { cellWidth: 38 },
        3: { cellWidth: 34 },
        4: { cellWidth: rectWidth - 146 },
      },
      didDrawCell: ({ cell, column, doc, section }) => {
        if (
          section !== "body" ||
          column.index !== 0 ||
          typeof cell.raw !== "object"
        ) {
          return;
        }

        const textPosition = cell.getTextPos();
        const textStyles = {
          halign: cell.styles.halign,
          valign: cell.styles.valign,
          maxWidth: Math.ceil(
            cell.width - cell.padding("left") - cell.padding("right"),
          ),
        };

        doc.setTextColor(100, 100, 100);
        doc.autoTableText(
          cell.text,
          textPosition.x + 0.1,
          textPosition.y + 0.1,
          textStyles,
        );
        doc.setTextColor(...cell.styles.textColor);
        doc.autoTableText(
          cell.text,
          textPosition.x,
          textPosition.y,
          textStyles,
        );
      },
    });

    y = pdf.lastAutoTable.finalY + 8;
  });

  if (y > pageHeight - 30) {
    pdf.addPage();
    y = 20;
  }

  pdf.setFont("helvetica", "normal");
  pdf.setDrawColor(0, 0, 0);
  pdf.setLineWidth(0.25);
  pdf.line(leftMargin, y, pageWidth - leftMargin, y);
  y += 6;
  pdf.autoTable({
    startY: y,
    head: [["", "Person", "Date"]],
    body: samplePdfAuditRows(sampleShipment).filter(hasPdfAuditValue),
    styles: { fontSize: 9, cellPadding: 2 },
    headStyles: {
      fillColor: [173, 216, 230],
      textColor: 0,
      fontStyle: "normal",
    },
    columnStyles: {
      0: { cellWidth: 35 },
      1: { cellWidth: 75 },
      2: { cellWidth: rectWidth - 110 },
    },
  });
  y = pdf.lastAutoTable.finalY + 10;

  pdf.save(
    `sample_shipment_confirmation_${pickupAddress.centerNumber || sampleShipment.centerNumber || "center"}_${sampleShipment.reference || "shipment"}.pdf`,
  );
};
